import React, { useState } from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { PeriodItemEntry } from 'api/queries/getSummaryTrackedActivities.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useSummaryTrackedActivitiesComponentLogic } from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.logic';
import { useSummaryTrackedActivitiesComponentProvider } from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.utils';

const elementId = 'summary-tracked-activities';
const id = 'summary-tracked-activities';
const componentKey = 'mySummaryTrackedActivities';
const title = `My Tracked Activities`;

const SummaryTrackedActivitiesComponent = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: ITableWidgetWrapperProps) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { api, ui } = useSummaryTrackedActivitiesComponentProvider({
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        contractId,
        widgetId,
        compareVersionId,
        table,
    });

    const { logic } = useSummaryTrackedActivitiesComponentLogic({ api });

    return (
        <WidgetWithTitle
            title={<TitleWithCounter title={title} counter={logic.activeData?.length} />}
            tooltip={api.getTrackedActivities.data?.info}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.isInPinboard && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : null,
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={elementId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
            ]}
        >
            <div className={classes.tableWrapperContainer}>
                {table.updatedInitialState && (
                    <TableV8<PeriodItemEntry>
                        data={logic.activeData}
                        columns={columnsV8}
                        isLoading={api.getTrackedActivities.isLoading || api.getTrackedActivities.isFetching}
                        noData={<NoData />}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualPagination={false}
                        manualSorting={false}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                    />
                )}
            </div>

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
        </WidgetWithTitle>
    );
};
export default React.memo(SummaryTrackedActivitiesComponent);
